import { useEffect, useState } from 'react';

interface Particle {
  id: number;
  shape: 'circle' | 'triangle' | 'square' | 'hexagon' | 'diamond';
  size: number;
  x: number;
  y: number;
  duration: number;
  delay: number;
  opacity: number;
  drift: number;
  rotate: number;
}

const generateParticles = (count: number): Particle[] => {
  const shapes: Particle['shape'][] = ['circle', 'triangle', 'square', 'hexagon', 'diamond'];
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    shape: shapes[i % shapes.length],
    size: 10 + Math.random() * 26,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: 12 + Math.random() * 18,
    delay: Math.random() * -20,
    opacity: 0.25 + Math.random() * 0.35,
    drift: -40 + Math.random() * 80,
    rotate: Math.random() * 360,
  }));
};

const particles = generateParticles(24);

function ParticleShape({ particle }: { particle: Particle }) {
  const isBlue = particle.id % 2 === 0;
  const borderColor = isBlue ? 'rgba(100,180,255,0.7)' : 'rgba(255,255,255,0.65)';
  const bgColor = isBlue ? 'rgba(100,180,255,0.15)' : 'rgba(255,255,255,0.1)';

  const baseStyle: React.CSSProperties = {
    position: 'absolute',
    left: `${particle.x}%`,
    top: `${particle.y}%`,
    width: particle.size,
    height: particle.size,
    opacity: 0,
    animation: `particleFloat${particle.id % 4} ${particle.duration}s ease-in-out ${particle.delay}s infinite`,
  };

  switch (particle.shape) {
    case 'circle':
      return (
        <div
          style={{ ...baseStyle, borderColor, backgroundColor: bgColor }}
          className="rounded-full border-2"
        />
      );
    case 'square':
      return (
        <div
          style={{ ...baseStyle, transform: `rotate(${particle.rotate}deg)`, borderColor, backgroundColor: bgColor }}
          className="border-2 rounded-sm"
        />
      );
    case 'diamond':
      return (
        <div
          style={{ ...baseStyle, transform: `rotate(45deg)`, borderColor, backgroundColor: bgColor }}
          className="border-2"
        />
      );
    case 'triangle':
      return (
        <div
          style={{
            ...baseStyle,
            width: 0,
            height: 0,
            borderLeft: `${particle.size / 2}px solid transparent`,
            borderRight: `${particle.size / 2}px solid transparent`,
            borderBottom: `${particle.size}px solid ${isBlue ? 'rgba(100,180,255,0.45)' : 'rgba(255,255,255,0.4)'}`,
            background: 'none',
          }}
        />
      );
    case 'hexagon':
      return (
        <div
          style={{
            ...baseStyle,
            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
            backgroundColor: isBlue ? 'rgba(100,180,255,0.25)' : 'rgba(255,255,255,0.2)',
            border: `2px solid ${borderColor}`,
          }}
        />
      );
    default:
      return null;
  }
}

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const titleWords = 'Centro de Desarrollo Empresarial MIPYME Región Valle de Sula'.split(' ');

  return (
    <section id="inicio" className="relative h-screen flex items-center justify-center overflow-hidden">
      <style>{`
        @keyframes wordReveal {
          0% {
            opacity: 0;
            transform: translateY(40px) rotateX(40deg);
            filter: blur(8px);
          }
          100% {
            opacity: 1;
            transform: translateY(0) rotateX(0deg);
            filter: blur(0px);
          }
        }
        @keyframes badgeSlide {
          0% {
            opacity: 0;
            transform: translateX(-30px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes fadeUp {
          0% {
            opacity: 0;
            transform: translateY(24px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes btnReveal {
          0% {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .word-animate {
          display: inline-block;
          opacity: 0;
          animation: wordReveal 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .badge-animate {
          opacity: 0;
          animation: badgeSlide 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .desc-animate {
          opacity: 0;
          animation: fadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .btn-animate {
          opacity: 0;
          animation: btnReveal 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }

        @keyframes particleFloat0 {
          0%, 100% {
            opacity: 0;
            transform: translateY(0px) translateX(0px) rotate(0deg) scale(0.5);
          }
          10% {
            opacity: var(--p-opacity, 0.3);
          }
          50% {
            transform: translateY(-80px) translateX(30px) rotate(180deg) scale(1);
          }
          90% {
            opacity: var(--p-opacity, 0.3);
          }
        }
        @keyframes particleFloat1 {
          0%, 100% {
            opacity: 0;
            transform: translateY(0px) translateX(0px) rotate(0deg) scale(0.6);
          }
          15% {
            opacity: var(--p-opacity, 0.28);
          }
          50% {
            transform: translateY(-60px) translateX(-40px) rotate(120deg) scale(1.1);
          }
          85% {
            opacity: var(--p-opacity, 0.28);
          }
        }
        @keyframes particleFloat2 {
          0%, 100% {
            opacity: 0;
            transform: translateY(0px) translateX(0px) rotate(0deg) scale(0.7);
          }
          12% {
            opacity: var(--p-opacity, 0.35);
          }
          50% {
            transform: translateY(-100px) translateX(20px) rotate(240deg) scale(0.9);
          }
          88% {
            opacity: var(--p-opacity, 0.35);
          }
        }
        @keyframes particleFloat3 {
          0%, 100% {
            opacity: 0;
            transform: translateY(0px) translateX(0px) rotate(0deg) scale(0.5);
          }
          8% {
            opacity: var(--p-opacity, 0.3);
          }
          50% {
            transform: translateY(-70px) translateX(-25px) rotate(300deg) scale(1.05);
          }
          92% {
            opacity: var(--p-opacity, 0.3);
          }
        }
      `}</style>

      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/b286a2ed3ca10247dabf9d64ba360efd/819bea9d1eed9acb6b72a02b79fc4185.jpeg"
          alt="Emprendedores del Valle de Sula"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
      </div>

      {/* Partículas geométricas flotantes */}
      <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
        {particles.map((p) => (
          <div
            key={p.id}
            style={{ '--p-opacity': p.opacity } as React.CSSProperties}
          >
            <ParticleShape particle={p} />
          </div>
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-16 w-full">
        <div className="max-w-3xl">
          <div
            className={`inline-block mb-6 px-4 py-2 border-2 border-white/80 rounded-full ${
              isVisible ? 'badge-animate' : 'opacity-0'
            }`}
            style={{ animationDelay: '0.1s' }}
          >
            <span className="text-white text-sm font-medium">Centro de Desarrollo Empresarial</span>
          </div>

          <h1
            className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight"
            style={{ fontFamily: "'Montserrat', sans-serif", perspective: '600px' }}
          >
            {titleWords.map((word, index) => (
              <span
                key={index}
                className={isVisible ? 'word-animate' : 'opacity-0'}
                style={{ animationDelay: `${0.3 + index * 0.08}s`, marginRight: '0.3em' }}
              >
                {word}
              </span>
            ))}
          </h1>

          <p
            className={`text-xl text-white/90 mb-10 leading-relaxed ${
              isVisible ? 'desc-animate' : 'opacity-0'
            }`}
            style={{ animationDelay: '1.1s' }}
          >
            Brindamos asistencia técnica, asesoría empresarial y capacitación especializada para impulsar el
            crecimiento y competitividad de tu negocio en la región.
          </p>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => scrollToSection('servicios')}
              className={`bg-[#0066CC] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#0052A3] transition-all duration-300 flex items-center space-x-2 whitespace-nowrap cursor-pointer ${
                isVisible ? 'btn-animate' : 'opacity-0'
              }`}
              style={{ animationDelay: '1.4s' }}
            >
              <span>Conoce Nuestros Servicios</span>
              <i className="ri-arrow-right-line"></i>
            </button>

            <button
              onClick={() => scrollToSection('programas')}
              className={`bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-all duration-300 whitespace-nowrap cursor-pointer ${
                isVisible ? 'btn-animate' : 'opacity-0'
              }`}
              style={{ animationDelay: '1.55s' }}
            >
              Ver Programas Vigentes
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
